import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { PasswordInput } from './ui/password-input';
import { CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import { getSupabaseConfig } from '../utils/supabase/config';
import { getStoredSessionToken } from '../utils/apiHeaders';
import { ManualSubdomainCreator } from './ManualSubdomainCreator';

interface BootstrapProps {
  onComplete: () => void;
}

export function Bootstrap({ onComplete }: BootstrapProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successData, setSuccessData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const config = getSupabaseConfig();
      const response = await fetch(
        `${config.url}/functions/v1/make-server-e90c59cd/bootstrap/create-system-admin`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${config.anonKey}`,
          },
          body: JSON.stringify({ 
            email, 
            password, 
            name
          }),
        }
      );

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse response JSON:', jsonError);
        console.error('Response status:', response.status);
        console.error('Response statusText:', response.statusText);
        setError(`Server error (${response.status}): ${response.statusText}`);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        console.error('Bootstrap failed with response:', data);
        console.error('Response status:', response.status);
        setError(data.error || `Failed to create admin account (${response.status})`);
        setLoading(false);
        return;
      }

      setSuccess(true);
      setSuccessData(data);
      
      // Wait 2 seconds then redirect to login
      setTimeout(() => {
        // Redirect to root domain login page (System Admin)
        window.location.href = '/login';
      }, 2000);

    } catch (err: any) {
      console.error('Bootstrap error:', err);
      console.error('Bootstrap error details:', {
        message: err.message,
        stack: err.stack,
        type: err.constructor.name
      });
      setError(err.message || 'Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('WARNING: This will delete ALL data from the database.\\n\\nThis includes all users, companies, projects, and configuration.\\n\\nAre you sure you want to continue?')) {
      return;
    }

    setResetting(true);
    setError('');
    setResetSuccess(false);

    try {
      const config = getSupabaseConfig();
      // The server resets only for a signed-in system admin; without a session it answers 401.
      const response = await fetch(
        `${config.url}/functions/v1/make-server-e90c59cd/bootstrap/reset`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStoredSessionToken() || config.anonKey}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to reset database');
        setResetting(false);
        return;
      }

      console.log('[BOOTSTRAP] Database reset successful:', data);
      setResetSuccess(true);
      setResetting(false);
      
      // Clear the error about existing users
      setError('');
    } catch (err: any) {
      console.error('Reset error:', err);
      setError(err.message || 'Failed to reset database');
      setResetting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <CardTitle>System Admin Created</CardTitle>
            </div>
            <CardDescription>
              Your system administrator account has been created successfully.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800 dark:text-green-200">
                <strong>Setup Complete</strong><br />
                Email: {email}<br />
                Role: System Administrator<br /><br />
                Redirecting to login page...
              </AlertDescription>
            </Alert>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Next Step:</strong><br />
                After logging in, use the <strong>"Seed Demo Data"</strong> button in the System Administration page to populate the system with sample companies, projects, and users for testing.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Pathkit System Setup</CardTitle>
          <CardDescription>
            Create the first system administrator account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {error}
                  {(error.includes('already has users') || error.includes('already exists')) && (
                    <div className="mt-3">
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={handleReset}
                        disabled={resetting}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {resetting ? 'Resetting...' : 'Reset All Data'}
                      </Button>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {resetSuccess && (
              <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800 dark:text-green-200">
                  <strong>Database Reset Complete</strong><br />
                  All data has been cleared. You can now create your system administrator account.
                </AlertDescription>
              </Alert>
            )}

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>First Time Setup:</strong> This creates your first system administrator account for platform management.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
              <p className="text-sm text-muted-foreground">
                Minimum 6 characters
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <PasswordInput
                id="confirmPassword"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Tip for staging.pathkit.pro:</strong><br />
                After creating your account and logging in, use the <strong>"Seed Demo Data"</strong> button in the System Administration page to populate the system with sample companies, projects, and users for testing.
              </AlertDescription>
            </Alert>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Creating Admin Account...' : 'Create System Admin'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Manual Subdomain Creator - shown after successful account creation */}
      {success && (
        <div className="mt-6">
          <ManualSubdomainCreator />
        </div>
      )}
    </div>
  );
}